import axios, { AxiosError, AxiosInstance } from 'axios';
import { format } from 'date-fns';
import { 
  connectResponseSchema, 
  createContactResponseSchema,
  submitApplicationResponseSchema,
  contactCenterAvailabilityResponseSchema,
  scheduleAppointmentResponseSchema,
  connectCallResponseSchema,
  availabilityResponseSchema,
  prospectVerifyResponseSchema,
  connectKbaResponseSchema,
  verifyKbaResponseSchema,
  partnerDataSchema,
  partnerPreloadResponseSchema,
  startAutoEchelonResponseSchema,
  getOpportunityStatusResponseSchema,
  leadDetailsResponseSchema,
  type ConnectResponse, 
  type CreateContactResponse,
  type SubmitApplicationResponse,
  type ContactCenterAvailabilityResponse,
  type ScheduleAppointmentResponse,
  type ConnectCallResponse,
  type AvailabilityResponse,
  type ProspectVerifyResponse,
  type ConnectKbaResponse,
  type VerifyKbaResponse,
  type PartnerData,
  type StartAutoEchelonResponse,
  type GetOpportunityStatusResponse,
  type LeadDetailsData,
  ApiError, 
  successPageResponseSchema,
  type SuccessPageResponse
} from '../types/api';
import { formatE164 } from '../utils/validation';

export interface ILeadsService {
  connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse>;
  verifyCode(code: string, phoneNumber: string, spinwheelId: string, email: string, hubspotRecordId: string): Promise<ConnectResponse>;
  createContact(spinwheelId: string, email: string, hubspotRecordId: string): Promise<CreateContactResponse>;
  submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    hubspotRecordId?: string
  ): Promise<SubmitApplicationResponse>;
  getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse>;
  scheduleAppointment(hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse>;
  connectCall(hubspotRecordId: string): Promise<ConnectCallResponse>;
  getAvailability(date: string): Promise<AvailabilityResponse>;
  createContactExperian(
    email: string,
    ssn: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse>;
  createManualLead(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode?: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse>;
  prospectVerify(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<ProspectVerifyResponse>;
  // Track success page variant shown to customer
  successPage(hubspotRecordId: string, variantId: string): Promise<SuccessPageResponse>;
  connectKba(
    email: string,
    phone: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    ssn: string,
    address: { street: string; city: string; state: string; zipCode: string },
    ipAddress?: string,
    marketingParams?: Record<string, string>
  ): Promise<ConnectKbaResponse>;
  verifyKba(
    spinwheelId: string,
    hubspotRecordId: string,
    answers: Record<string, string>,
    email: string
  ): Promise<VerifyKbaResponse>;
  getPartnerPreload(partnerId: string): Promise<PartnerData>;
  startAutoEchelon(hubspotRecordId: string): Promise<StartAutoEchelonResponse>;
  getOpportunityStatus(hubspotRecordId: string, opportunityId: string): Promise<GetOpportunityStatusResponse>;
  getLeadDetails(leadUniqueId: string): Promise<LeadDetailsData>;
}

export class LeadsService implements ILeadsService {
  private readonly api: AxiosInstance;

  constructor(baseUrl: string, apiKey: string) {
    if (!baseUrl || !apiKey) {
      throw new Error('API configuration missing. Please check environment variables.');
    }

    this.api = axios.create({
      baseURL: baseUrl,
      headers: {
        'x-api-key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 60000, // 60 second timeout
      validateStatus: status => status >= 200 && status < 300 // Only resolve for 2xx status codes
    });

    // Add response interceptor for global error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.data?.status) {
          throw new ApiError(
            error.response.data.status.message,
            error.response.data.status
          );
        }
        throw error;
      }
    );
  }

  async connectBySms(
    phoneNumber: string,
    email: string,
    dateOfBirth: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse> {
    try {
      const response = await this.api.post('/leads/connect', {
        email,
        phone: formatE164(phoneNumber),
        dateOfBirth,
        ...(loanAmount !== undefined && { loanAmount }),
        ...(loanPurpose !== undefined && { loanPurpose }),
        ...(employmentStatus !== undefined && { employmentStatus }),
        ...(employmentFrequency !== undefined && { employmentPayFrequency: employmentFrequency }),
        ...(educationLevel !== undefined && { educationLevel }),
        ...(annualIncome !== undefined && { annualIncome }),
        ...(propertyStatus !== undefined && { propertyStatus }),
        ipAddress,
        marketingParams
      });

      return connectResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while connecting');
      }
      throw new Error('Failed to create connection');
    }
  }

  async verifyCode(
    code: string,
    phoneNumber: string,
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<ConnectResponse> {
    try {
      const response = await this.api.post('/leads/verify', {
        code,
        phoneNumber: formatE164(phoneNumber),
        spinwheelId,
        email,
        hubspotRecordId
      });

      return connectResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while verifying code');
      }
      throw new Error('Failed to verify code');
    }
  }

  async createContact(
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<CreateContactResponse> {
    try {
      const response = await this.api.post(
        '/leads/create',
        { spinwheelId, email, hubspotRecordId },
        { validateStatus: (status) => (status >= 200 && status < 300) || status === 422 }
      );

      return createContactResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while creating contact');
      }
      throw new Error('Failed to create contact');
    }
  }

  async submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    hubspotRecordId?: string
  ): Promise<SubmitApplicationResponse> {
    try {
      // Ensure SSN last 4 is exactly 4 digits with leading zeros preserved
      const formattedSsnLast4 = (ssnLast4 || '0000').padStart(4, '0').slice(-4);

      const response = await this.api.post('/leads/offers', {
        ...(email ? { email } : {}),
        ...(hubspotRecordId ? { hubspotRecordId } : {}),
        sensitiveLastFourSsn: formattedSsnLast4,
        ...(loanAmount !== undefined && { loanAmount }),
        ...(loanPurpose !== undefined && { loanPurpose }),
        ...(employmentStatus !== undefined && { employmentStatus }),
        ...(employmentFrequency !== undefined && { employmentPayFrequency: employmentFrequency }),
        ...(educationLevel !== undefined && { educationLevel }),
        ...(annualIncome !== undefined && { annualIncome }),
        ...(propertyStatus !== undefined && { propertyStatus }),
        ipAddress
      });

      return submitApplicationResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while submitting application');
      }
      throw new Error('Failed to submit application');
    }
  }

  async getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse> {
    try {
      const response = await this.api.get('/leads/contact-center/availability');
      return contactCenterAvailabilityResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while checking availability');
      }
      throw new Error('Failed to check contact center availability');
    }
  }

  async scheduleAppointment(hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse> {
    try {
      const response = await this.api.post(`/leads/${hubspotRecordId}/appointment`, {
        meetingCallTime
      });

      return scheduleAppointmentResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while scheduling appointment');
      }
      throw new Error('Failed to schedule appointment');
    }
  }

  async connectCall(hubspotRecordId: string): Promise<ConnectCallResponse> {
    try {
      const response = await this.api.post('/leads/call', {
        hubspotRecordId
      });

      return connectCallResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while connecting call');
      }
      throw new Error('Failed to connect call');
    }
  }

  async getAvailability(date: string): Promise<AvailabilityResponse> {
    try {
      // Ensure date is in yyyy-MM-dd format
      const formattedDate = this.formatDate(date);
      const response = await this.api.get(`/leads/availability?date=${formattedDate}`);
      return availabilityResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while fetching availability');
      }
      throw new Error('Failed to fetch availability');
    }
  }

  private formatDate(dateInput: string | Date): string {
    try {
      // If it's already a string and matches yyyy-MM-dd format, return as-is
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }
      
      // Convert to Date object and format
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided');
      }
      
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      throw new Error(`Invalid date format: ${dateInput}. Expected yyyy-MM-dd format.`);
    }
  }

  async createContactExperian(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    dateOfBirth?: string,
    phone?: string,
    ssn?: string

  ): Promise<CreateContactResponse> {
    try {
      const payload = {
        ssn,
        personalInfo: {
          firstName,
          lastName,
          email,
          phone: phone || '',
          dateOfBirth: dateOfBirth || ''
        },
        addressInfo: {
          street,
          city,
          state,
          zipCode: zip
        }
      };

      const response = await this.api.post('/leads/create/experian', payload);
      return createContactResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while checking eligibility');
      }
      throw new Error('Failed to verify with backup provider');
    }
  }

  async createManualLead(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode?: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse> {
    try {
      const response = await this.api.post('/leads/manual', {
        personalInfo: {
          firstName,
          lastName,
          email,
          phone: phone || '',
          dateOfBirth: dateOfBirth || ''
        },
        addressInfo: {
          street,
          city,
          state,
          zipCode: zip
        },
        ...(offerCode ? { offerCode } : {})
      });
      return createContactResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred during manual verification');
      }
      throw new Error('Failed to submit manual verification');
    }
  }

  async prospectVerify(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<ProspectVerifyResponse> {
    try {
      const response = await this.api.post('/leads/prospect-verify', {
        personalInfo: {
          firstName,
          lastName,
          email,
          phone: phone || '',
          dateOfBirth: dateOfBirth || ''
        },
        addressInfo: {
          street,
          city,
          state,
          zipCode: zip
        },
        offerCode
      });
      return prospectVerifyResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred during prospect verification');
      }
      throw new Error('Failed to verify prospect identity');
    }
  }

  async connectKba(
    email: string,
    phone: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    ssn: string,
    address: { street: string; city: string; state: string; zipCode: string },
    ipAddress?: string,
    marketingParams?: Record<string, string>
  ): Promise<ConnectKbaResponse> {
    try {
      const response = await this.api.post('/leads/connect/kba', {
        email,
        phone: formatE164(phone),
        firstName,
        lastName,
        dateOfBirth,
        ssn,
        address,
        ipAddress,
        userAgent: navigator.userAgent,
        marketingParams
      });
      return connectKbaResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while connecting KBA');
      }
      throw new Error('Failed to connect KBA');
    }
  }

  async verifyKba(
    spinwheelId: string,
    hubspotRecordId: string,
    answers: Record<string, string>,
    email: string
  ): Promise<VerifyKbaResponse> {
    try {
      const response = await this.api.post('/leads/verify/kba', {
        spinwheelId,
        hubspotRecordId,
        answers,
        email
      });
      return verifyKbaResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while verifying KBA answers');
      }
      throw new Error('Failed to verify KBA answers');
    }
  }

  // Track success page variant shown to customer
  async successPage(hubspotRecordId: string, variantId: string): Promise<SuccessPageResponse> {
    try {
      // Backend expects 'availability' field with variant ID that includes availability prefix
      // e.g., 'high-availability-auto-call', 'low-outside-call-hours'
      const response = await this.api.post(`/leads/${hubspotRecordId}/thank-you`, { 
        availability: variantId 
      });
      return successPageResponseSchema.parse(response.data);
    } catch (error) {
      return { status: { code: 200, message: 'Success' } } as SuccessPageResponse;
    }
  }

  async getPartnerPreload(partnerId: string): Promise<PartnerData> {
    try {
      const response = await this.api.get(`/leads/preload/${partnerId}`);
      return partnerPreloadResponseSchema.parse(response.data).data;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof AxiosError) throw new Error('Network error fetching partner preload data');
      throw new Error('Failed to fetch partner preload data');
    }
  }

  async startAutoEchelon(hubspotRecordId: string): Promise<StartAutoEchelonResponse> {
    try {
      const response = await this.api.post(`/leads/${hubspotRecordId}/auto-echelon`, {});
      return startAutoEchelonResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof AxiosError) throw new Error('Network error occurred while starting auto-echelon');
      throw new Error('Failed to start auto-echelon process');
    }
  }

  async getOpportunityStatus(hubspotRecordId: string, opportunityId: string): Promise<GetOpportunityStatusResponse> {
    try {
      const response = await this.api.get(`/leads/${hubspotRecordId}/opportunity/${opportunityId}/get-status`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      return getOpportunityStatusResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof AxiosError) throw new Error('Network error occurred while getting opportunity status');
      throw new Error('Failed to get opportunity status');
    }
  }

  async getLeadDetails(leadUniqueId: string): Promise<LeadDetailsData> {
    try {
      const response = await this.api.get(`/leads/${leadUniqueId}/details`);
      return leadDetailsResponseSchema.parse(response.data).data;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof AxiosError) throw new Error('Network error occurred while fetching lead details');
      throw new Error('Failed to fetch lead details');
    }
  }
}